import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type FeishuDepartmentsResponse,
  type FeishuDeptMembersResponse,
  type FeishuDeptNode,
  type RosterSyncResult,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import {
  feishuDepartmentsPath,
  feishuDeptMembersPath,
  feishuSyncPath,
} from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { InlineLoading } from "../../components/Spinner.js";
import { PrimaryButton } from "../../components/FormField.js";
import { cn } from "../../lib/cn.js";
import { formatDateTime } from "../../lib/format.js";
import type { OrgDetailContext } from "./context.js";

/**
 * Phase C.2 — 飞书部门树预览。
 * 数据源：mongo FeishuDepartmentModel（不实时拉外部 API；按 "重新同步" 触发 POST /sync）。
 * 兼任 RolesPanel suggestedFeishuDeptIds + UsersPanel bind picker + BulkImportDialog single-pick。
 */
export function FeishuDeptTree(props: {
  /** 选中 dept ids（picker 模式）。未传 = view-only。 */
  selected?: ReadonlyArray<string>;
  /** picker 模式回调；未传 = view-only。 */
  onToggle?: (openDepartmentId: string) => void;
  /**
   * picker 行为：
   * - `"multi"`（默认）：checkbox，调用方对每个 id 自己 toggle in/out
   * - `"single"`：radio + 整行可点；调用方收到回调后直接覆盖 selected
   */
  mode?: "single" | "multi";
  /** true = 隐藏顶部"最后同步 / 重新同步"控件（嵌入 picker 场景）。 */
  hideSyncHeader?: boolean;
  /**
   * true = 每个部门节点可展开「成员」列表（懒加载 live API，标组长/已导入）。
   * 组织搭建 wizard Step2 用，让管理者一眼看到部门里有哪些人。
   */
  showMembers?: boolean;
  /** 外部触发刷新：值递增时调用 refetch() 重拉部门树缓存。 */
  refreshKey?: number;
}): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSync, setLastSync] = useState<RosterSyncResult | null>(null);
  const { data, loading, error, refetch } = useApiQuery<FeishuDepartmentsResponse>(
    feishuDepartmentsPath(org.code),
    [org.code],
  );
  const prevRefreshKey = useRef(props.refreshKey);
  useEffect(() => {
    if (
      props.refreshKey !== undefined &&
      props.refreshKey !== prevRefreshKey.current
    ) {
      prevRefreshKey.current = props.refreshKey;
      void refetch();
    }
  }, [props.refreshKey, refetch]);
  // 所有 hook 在 early-return 之前，避免 Rules of Hooks 违反（loading→done 切换时 hook 数变化）。
  const selectedSet = useMemo(
    () => new Set(props.selected ?? []),
    [props.selected],
  );
  // 实例级唯一 radio name，防止两棵 single 树同屏 toggle 串扰
  const radioGroup = useId();
  const mode = props.mode ?? "multi";

  const handleSync = async (): Promise<void> => {
    if (!auth) return;
    setSyncing(true);
    setSyncError(null);
    try {
      const res = await apiCall<{ ok: true; summary: RosterSyncResult }>(
        "POST",
        feishuSyncPath(org.code),
        { token: auth.token },
      );
      setLastSync(res.summary);
      await refetch();
    } catch (err) {
      if (err instanceof ApiError) setSyncError(err.message);
      else setSyncError(String(err));
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <InlineLoading />;
  if (error) return <ErrorBanner>{error.message}</ErrorBanner>;
  if (!data) return <Empty title="暂无数据" />;

  return (
    <div className="flex flex-col gap-3">
      {props.hideSyncHeader ? null : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-dosia-text-dim">
              最后同步：
              {data.syncedAt ? formatDateTime(data.syncedAt) : "尚未同步"}
            </p>
            <PrimaryButton onClick={() => void handleSync()} disabled={syncing}>
              {syncing ? "同步中..." : "重新同步"}
            </PrimaryButton>
          </div>
          {syncError ? <ErrorBanner>{syncError}</ErrorBanner> : null}
          {lastSync ? (
            <p className="text-xs text-dosia-text-secondary">
              同步完成：{lastSync.departments} 部门 / {lastSync.usersUpdated} 用户更新
            </p>
          ) : null}
        </>
      )}
      {data.tree.length === 0 ? (
        <Empty
          title="尚无部门数据"
          description="点右上角'重新同步'拉一次飞书 roster"
        />
      ) : (
        <ul className="flex flex-col gap-0.5 text-sm">
          {data.tree.map((node) => (
            <DeptNode
              key={node.openDepartmentId}
              node={node}
              depth={0}
              selectedSet={selectedSet}
              onToggle={props.onToggle}
              mode={mode}
              radioGroup={radioGroup}
              orgCode={org.code}
              showMembers={props.showMembers ?? false}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function DeptNode({
  node,
  depth,
  selectedSet,
  onToggle,
  mode,
  radioGroup,
  orgCode,
  showMembers,
}: {
  node: FeishuDeptNode;
  depth: number;
  selectedSet: Set<string>;
  onToggle?: (id: string) => void;
  mode: "single" | "multi";
  radioGroup: string;
  orgCode: string;
  showMembers: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(depth < 1);
  const [membersOpen, setMembersOpen] = useState(false);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedSet.has(node.openDepartmentId);
  const rowClickable = onToggle != null && mode === "single";
  const canShowMembers = showMembers && node.memberCount > 0;
  return (
    <li>
      <div
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1 hover:bg-dosia-bg-lighter",
          isSelected && "bg-dosia-accent/15",
          rowClickable && "cursor-pointer",
        )}
        style={{ marginLeft: depth * 16 }}
        onClick={
          rowClickable ? () => onToggle?.(node.openDepartmentId) : undefined
        }
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
            aria-label={open ? "收起" : "展开"}
            className="w-4 text-dosia-text-secondary"
          >
            {open ? "▾" : "▸"}
          </button>
        ) : (
          <span className="w-4" />
        )}
        {onToggle ? (
          <input
            type={mode === "single" ? "radio" : "checkbox"}
            name={mode === "single" ? radioGroup : undefined}
            checked={isSelected}
            onChange={() => onToggle(node.openDepartmentId)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`选择 ${node.name}`}
          />
        ) : null}
        <span className="text-dosia-text">{node.name}</span>
        <span className="text-xs text-dosia-text-dim font-mono ml-1">
          {node.openDepartmentId}
        </span>
        <span className="ml-auto flex items-center gap-1 text-xs">
          {canShowMembers ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMembersOpen((o) => !o);
              }}
              className={cn(
                "rounded-full px-1.5 py-0.5 transition-colors duration-150",
                membersOpen
                  ? "bg-dosia-accent/20 text-dosia-text"
                  : "text-dosia-text-secondary hover:bg-dosia-bg-lighter",
              )}
            >
              {node.memberCount} 人 {membersOpen ? "▴" : "▾"}
            </button>
          ) : (
            <span className="text-dosia-text-secondary">{node.memberCount} 人</span>
          )}
          {node.boundUserCount > 0 ? (
            <span className="rounded-full bg-emerald-400/15 text-emerald-700 px-1.5 py-0.5 text-[10px]">
              {node.boundUserCount} 已绑
            </span>
          ) : null}
        </span>
      </div>
      {canShowMembers && membersOpen ? (
        <DeptMemberList
          orgCode={orgCode}
          openDepartmentId={node.openDepartmentId}
          leaderUserIds={node.leaderUserIds}
          depth={depth}
        />
      ) : null}
      {open && hasChildren ? (
        <ul className="flex flex-col gap-0.5">
          {node.children.map((child) => (
            <DeptNode
              key={child.openDepartmentId}
              node={child}
              depth={depth + 1}
              selectedSet={selectedSet}
              onToggle={onToggle}
              mode={mode}
              radioGroup={radioGroup}
              orgCode={orgCode}
              showMembers={showMembers}
            />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * 部门成员懒加载列表（仅在节点展开成员时挂载 → 触发 fetch）。
 * 显示姓名 / openId / 组长 / 已导入。来源：飞书 live API（fetchChild=true）。
 */
function DeptMemberList({
  orgCode,
  openDepartmentId,
  leaderUserIds,
  depth,
}: {
  orgCode: string;
  openDepartmentId: string;
  leaderUserIds: string[];
  depth: number;
}): JSX.Element {
  const { data, loading, error } = useApiQuery<FeishuDeptMembersResponse>(
    feishuDeptMembersPath(orgCode, openDepartmentId),
    [orgCode, openDepartmentId],
  );
  const leaderSet = useMemo(() => new Set(leaderUserIds), [leaderUserIds]);
  const indent = (depth + 1) * 16 + 24;

  if (loading) {
    return (
      <div style={{ marginLeft: indent }} className="py-1.5">
        <InlineLoading label="加载成员…" />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ marginLeft: indent }} className="py-1.5">
        <ErrorBanner>{error.message}</ErrorBanner>
      </div>
    );
  }
  const members = data?.members ?? [];
  if (members.length === 0) {
    return (
      <p
        style={{ marginLeft: indent }}
        className="py-1.5 text-xs text-dosia-text-dim"
      >
        该部门下暂无成员
      </p>
    );
  }
  return (
    <ul
      style={{ marginLeft: indent }}
      className="flex flex-col gap-px border-l border-dosia-border-light pl-2 py-1"
    >
      {members.map((m) => {
        const isLeader = m.isLeader || leaderSet.has(m.openId);
        return (
          <li
            key={m.openId}
            className="flex items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-dosia-bg-lighter"
          >
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-dosia-bg-lighter text-[10px] font-medium text-dosia-text-secondary">
              {(m.name ?? "?").slice(0, 1)}
            </span>
            <span className="text-dosia-text">{m.name ?? "未命名"}</span>
            <span className="font-mono text-dosia-text-dim">{m.openId.slice(0, 12)}…</span>
            {isLeader ? (
              <span className="rounded-full bg-dosia-accent/20 px-1.5 py-0.5 text-[10px] text-dosia-text">
                组长
              </span>
            ) : null}
            {m.alreadyImported ? (
              <span className="rounded-full bg-emerald-400/15 px-1.5 py-0.5 text-[10px] text-emerald-700">
                已导入
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
